import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  XIcon,
  PackageIcon,
  MapPinIcon,
  UserIcon,
  PhoneIcon,
  ClockIcon,
} from "lucide-react";
import { pickPrimaryImageUrl } from "../utils/pickPrimaryImageUrl";
import { formatDeliveryAddressOrFallback } from "../utils/formatDeliveryAddress";
import {
  type DeliverySlotInfoLike,
  resolveOrderSlotDisplay,
} from "../utils/deliveryTimeSlots";

export type OrderLikeForDetails = {
  id?: number;
  order_number?: string;
  items?: Array<{
    id?: number;
    quantity?: number;
    product?: Record<string, unknown> | null;
  }>;
  delivery_slot_info?: DeliverySlotInfoLike | null;
  delivery_time_slot?: string;
  delivery_time?: string;
  delivery_address?: Record<string, unknown> | null;
  addressDisplay?: string;
};

function getAddressDisplay(order: OrderLikeForDetails): string {
  if (order.addressDisplay?.trim()) {
    return formatDeliveryAddressOrFallback({
      address_line1: order.addressDisplay.trim(),
    });
  }
  return formatDeliveryAddressOrFallback(order.delivery_address);
}

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderLikeForDetails | null;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const [preview, setPreview] = useState<{ src: string; label: string } | null>(
    null
  );

  const closePreview = useCallback(() => setPreview(null), []);

  useEffect(() => {
    if (!isOpen || !order) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, order]);

  useEffect(() => {
    if (!isOpen) setPreview(null);
  }, [isOpen]);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview, closePreview]);

  if (!isOpen || !order) return null;

  const items = Array.isArray(order.items) ? order.items : [];
  const addr = order.delivery_address || {};
  const customerName =
    (typeof addr.receiver_name === "string" && addr.receiver_name) || "Customer";
  const phone =
    (typeof addr.receiver_phone === "string" && addr.receiver_phone) || "—";
  const slot = resolveOrderSlotDisplay(order);
  const orderLabel =
    order.order_number || (order.id != null ? `#${order.id}` : "Order");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.18)] ring-1 ring-stone-200/80 max-h-[90dvh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-details-title"
      >
        <div className="h-1 w-full shrink-0 bg-orange-500" aria-hidden />

        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-1 shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-stone-400">Order</p>
            <h3 id="order-details-title" className="text-lg font-semibold text-stone-900 tracking-tight truncate">{orderLabel}</h3>
            {slot ? (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-stone-500">
                <ClockIcon size={15} className="text-orange-400 shrink-0" aria-hidden />
                <span className="truncate">{slot}</span>
              </div>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" aria-label="Close">
            <XIcon size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          <section className="space-y-3">
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                <UserIcon size={18} aria-hidden />
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="text-xs text-stone-400">Recipient</p>
                <p className="text-sm font-medium text-stone-900">{customerName}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500">
                <PhoneIcon size={18} aria-hidden />
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="text-xs text-stone-400">Phone</p>
                <p className="text-sm font-medium text-stone-900">{phone}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500">
                <MapPinIcon size={18} aria-hidden />
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="text-xs text-stone-400">Address</p>
                <p className="text-sm leading-relaxed text-stone-700 break-words">
                  {getAddressDisplay(order)}
                </p>
              </div>
            </div>
          </section>

          <section>
            <p className="mb-2 text-xs font-medium text-stone-400">
              Items <span className="text-stone-300">·</span> {items.length}
            </p>
            {items.length === 0 ? (
              <p className="rounded-xl bg-stone-50 px-3 py-3 text-sm text-stone-500">
                No line items returned for this order.
              </p>
            ) : (
              <ul className="space-y-2">
                {items.map((line, idx) => {
                  const p = line.product;
                  const name =
                    (p &&
                      typeof p === "object" &&
                      typeof p.name === "string" &&
                      p.name) ||
                    "Item";
                  const category =
                    p && typeof p === "object" && typeof p.category_name === "string"
                      ? p.category_name
                      : "";
                  const qty = line.quantity ?? 1;
                  const thumb = pickPrimaryImageUrl(p, "thumb");
                  const full =
                    pickPrimaryImageUrl(p, "full") || pickPrimaryImageUrl(p, "card") || thumb;
                  const key = line.id ?? idx;

                  return (
                    <li
                      key={key}
                      className="flex gap-3 rounded-xl bg-stone-50/90 px-3 py-2.5 ring-1 ring-stone-100"
                    >
                      {thumb ? (
                        <button
                          type="button"
                          className="h-12 w-12 shrink-0 rounded-lg overflow-hidden ring-1 ring-stone-200 cursor-pointer hover:ring-orange-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 active:scale-[0.98] transition-shadow"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreview({ src: full || thumb, label: name });
                          }}
                          aria-label={`View image for ${name}`}
                        >
                          <img
                            src={thumb}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-stone-100">
                          <PackageIcon size={20} className="text-stone-300" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1 py-0.5">
                        <p className="text-sm font-medium leading-snug text-stone-900">
                          {name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {category ? (
                            <span className="text-[11px] text-stone-500">{category}</span>
                          ) : null}
                          <span className="text-[11px] font-medium tabular-nums text-stone-600">
                            ×{qty}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* <div className="border-t border-stone-100 px-5 py-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl py-2.5 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50"
          >
            Done
          </button>
        </div> */}

      </div>

      {preview ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 p-4"
          onClick={closePreview}
          role="dialog"
          aria-modal="true"
          aria-label={`Product image: ${preview.label}`}
        >
          <button
            type="button"
            onClick={closePreview}
            className="absolute top-3 right-3 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Close image"
          >
            <XIcon size={24} />
          </button>
          <img
            src={preview.src}
            alt={preview.label}
            className="max-h-[85dvh] max-w-full rounded-lg object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-0 right-0 text-center text-sm text-white/90 px-4 truncate">
            {preview.label}
          </p>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default OrderDetailsModal;
